import React from 'react';
import classNames from 'classnames';

import './Multiselect.scss';
import BaseProps from "../../types/BaseProps";
import Icon from "../../common/Icon";

export type Item = {
  label: string;
  value: any;
}

type MultiselectState = {
  items: Array<Item>;
  selectedItems?: Array<Item>;
}

/**
 * @param items Should contain the list with available for selection items.
 * @param selectedItems Should optionally contain the list with preselected items.
 * @param filter Is a callback function which is used for filtering out the provided item from the
 * items or selectedItems lists.
 * @param onChange Is a callback handler which is triggered on every selection change.
 */
type MultiselectProps = {
  items: Array<Item>;
  selectedItems: Array<Item>;
  filter: (items: Array<Item>, remove: Item) => Array<Item>;
  onChange: (items: Array<Item>) => void;
}

/**
 * Component which allows two-sided selection of multiple items.
 */
class Multiselect extends React.Component<MultiselectProps & BaseProps, MultiselectState> {

  constructor(props: any) {
    super(props);
    this.state = {
      items: props.items,
      selectedItems: props.selectedItems
    };
    this.selectItem = this.selectItem.bind(this);
    this.deselectItem = this.deselectItem.bind(this);
    this.selectAll = this.selectAll.bind(this);
    this.deselectAll = this.deselectAll.bind(this);
  }

  onChange() {
    this.props.onChange(this.state.selectedItems || []);
  }

  selectItem(selected: Item) {
    const selectedItems = this.state.selectedItems || [];
    this.setState({
      items: this.props.filter(this.state.items, selected),
      selectedItems: (selectedItems.concat([selected]))
    }, this.onChange);
  }

  deselectItem(deselected: Item) {
    const selectedItems = this.state.selectedItems || [];
    this.setState({
      items: (this.state.items && this.state.items.concat([deselected])) || [],
      selectedItems: this.props.filter(selectedItems, deselected)
    }, this.onChange);
  }

  selectAll() {
    const selectedItems = this.state.selectedItems || [];
    this.setState({
      items: [],
      selectedItems: selectedItems.concat([...this.state.items])
    }, this.onChange);
  }

  deselectAll() {
    const selectedItems = this.state.selectedItems || [];
    this.setState({
      items: this.state.items.concat([...selectedItems]),
      selectedItems: []
    }, this.onChange);
  }

  render() {
    return (
      <div className={classNames("Multiselect border", this.props.className)}
           style={this.props.style}>
        <div className="available-items items border-right">
          <div className="controls">
            <button className="select-all" onClick={this.selectAll}>select all</button>
          </div>
          <ul className="border-top">
            {
              this.state.items.map((item, ind) => (
                <ItemComponent key={ind} index={ind} item={item} action={this.selectItem}/>
              ))
            }
          </ul>
        </div>
        <div className="arrows">
          <Icon icon="exchange-alt"/>
        </div>
        <div className="selected-items items border-left">
          <div className="controls">
            <button className="clear-all" onClick={this.deselectAll}>clear all</button>
          </div>
          <ul className="border-top">
            {
              this.state.selectedItems && this.state.selectedItems.map((item, ind) => (
                <ItemComponent key={ind} index={ind} item={item} action={this.deselectItem}/>
              ))
            }
          </ul>
        </div>
      </div>
    );
  }

}

function ItemComponent(props: any) {
  return <li className="user-select-none"
             onClick={() => props.action(props.item)}>{props.item.label}</li>
}

export default Multiselect;
